import { useTranslation } from "react-i18next";

/**
 * useLockCopy
 * -------------------------------------------------------------------------
 * Centralise le WORDING des verrous premium (et rien d'autre).
 *
 * Référence : documentation/docs/ux-audit/locks-premium-audit.md (§4, §9).
 *
 * Ce hook NE décide PAS si une feature est verrouillée, NE lit aucun quota,
 * NE connaît aucun plan. Il résout uniquement le texte i18n à afficher pour
 * une `featureKey` donnée, avec des fallbacks sûrs.
 *
 * Clés i18n (namespace "review") :
 *   commercial.lock.eyebrow
 *   commercial.lock.viewOptions
 *   commercial.lock.byFeature.<featureKey>.short
 *   commercial.lock.byFeature.<featureKey>.long
 *   commercial.lock.byFeature.<featureKey>.tooltip
 *   commercial.lock.byFeature.<featureKey>.cta
 *   commercial.lock.byFeature.<featureKey>.value1|value2|value3
 *
 * Les anciennes clés (commercial.lock.availableWith / upgrade / unlock) restent
 * disponibles en fallback de dernier recours.
 *
 * @param {string} [featureKey] identifiant stable de la feature gatée.
 * @param {object} [options]
 * @param {object} [options.overrides] surcharges explicites { eyebrow, short, long, tooltip, cta, valueBullets }.
 * @returns {{ eyebrow:string, short:string, long:string, tooltip:string, cta:string, valueBullets:string[] }}
 */
const useLockCopy = (featureKey, options = {}) => {
    const { t } = useTranslation("review");
    const { overrides = {} } = options;

    const base = featureKey ? `commercial.lock.byFeature.${featureKey}` : null;

    const eyebrow = overrides.eyebrow
        || t("commercial.lock.eyebrow", { defaultValue: "Plan supérieur" });

    // Fallback générique partagé (ancien wording conservé pour compat).
    const genericShort = t("commercial.lock.availableWith", {
        defaultValue: "Plan supérieur",
    });
    const genericCta = t("commercial.lock.viewOptions", {
        defaultValue: "Voir les options",
    });

    const short = overrides.short
        || (base ? t(`${base}.short`, { defaultValue: genericShort }) : genericShort);

    const long = overrides.long
        || (base ? t(`${base}.long`, { defaultValue: short }) : short);

    const tooltip = overrides.tooltip
        || (base ? t(`${base}.tooltip`, { defaultValue: long }) : long);

    const cta = overrides.cta
        || (base ? t(`${base}.cta`, { defaultValue: genericCta }) : genericCta);

    let valueBullets = overrides.valueBullets;
    if (!Array.isArray(valueBullets)) {
        valueBullets = [];
        if (base) {
            ["value1", "value2", "value3"].forEach((key) => {
                const value = t(`${base}.${key}`, { defaultValue: "" });
                if (value) valueBullets.push(value);
            });
        }
    }

    return { eyebrow, short, long, tooltip, cta, valueBullets };
};

export default useLockCopy;

